import { z } from 'zod';

/** Hebrew labels live in ./he.ts; schemas describe data shape only. */

export const FAIR_STATES = ['before', 'keynote', 'sessions', 'ended'] as const;
export const FairStateSchema = z.enum(FAIR_STATES);
export type FairState = z.infer<typeof FairStateSchema>;

export const UNIT_STATUSES = ['published', 'draft', 'hidden'] as const;
export const UnitStatusSchema = z.enum(UNIT_STATUSES);
export type UnitStatus = z.infer<typeof UnitStatusSchema>;

export const RoundSchema = z.object({ start: z.string(), end: z.string() });
export type Round = z.infer<typeof RoundSchema>;

export const PdfSchema = z.object({ title: z.string(), url: z.string() });
export type Pdf = z.infer<typeof PdfSchema>;

/** A participating unit (booth). */
export const UnitSchema = z.object({
  id: z.string().min(1),
  name: z.string(),
  nameAr: z.string().default(''),
  nameEn: z.string().default(''),
  booth: z.string().default(''),
  domain: z.string().default(''),
  region: z.string().default(''),
  description: z.string().default(''),
  keywords: z.array(z.string()).default([]),
  pdfs: z.array(PdfSchema).default([]),
  youtube: z.string().default(''),
  teams: z.string().default(''),
  logo: z.string().default(''),
  symbol: z.string().default(''),
  status: UnitStatusSchema.default('draft'),
  updatedAt: z.number().default(0),
  updatedBy: z.string().default(''),
});
export type Unit = z.infer<typeof UnitSchema>;

export const FaqSchema = z.object({ q: z.string(), a: z.string() });

export const CONSULT_OPEN = ['sessions', 'event', 'always'] as const;
export const ConsultSchema = z.object({
  enabled: z.boolean().default(false),
  title: z.string().default(''),
  text: z.string().default(''),
  teams: z.string().default(''),
  open: z.enum(CONSULT_OPEN).default('event'),
  faq: z.array(FaqSchema).default([]),
  faqPdf: PdfSchema.nullable().default(null),
  email: z.string().default(''),
  phone: z.string().default(''),
});
export type Consult = z.infer<typeof ConsultSchema>;

export const VideoSchema = z.object({
  url: z.string().default(''),
  title: z.string().default(''),
  duration: z.string().default(''),
  poster: z.string().default(''),
});
export type Video = z.infer<typeof VideoSchema>;

/** Fields that drive the event phase. Shared by admin settings and the public state file. */
export const ScheduleSchema = z.object({
  /** YYYY-MM-DD, Asia/Jerusalem. */
  date: z.string(),
  /** HH:MM, Asia/Jerusalem. */
  convStart: z.string(),
  convEnd: z.string(),
  rounds: z.tuple([RoundSchema, RoundSchema, RoundSchema]),
  mode: z.enum(['auto', 'manual']).default('auto'),
  manualState: FairStateSchema.default('before'),
  /** Minutes added to all three rounds. */
  delay: z.number().default(0),
});
export type Schedule = z.infer<typeof ScheduleSchema>;

/** Static site content. Changing it requires publishing a new data snapshot. */
export const SiteContentSchema = z.object({
  fairName: z.string(),
  org: z.string(),
  intro: z.string().default(''),
  tagline: z.string().default(''),
  heroMode: z.enum(['image', 'designed']).default('image'),
  heroImage: z.string().default(''),
  heroMobile: z.string().default(''),
  heroAlt: z.string().default(''),
  /** CSS color shown beside the hero image. Sampled from the image by the admin at upload time. */
  heroBg: z.string().default(''),
  logoMoj: z.string().default(''),
  logoCodex: z.string().default(''),
  accUrl: z.string().default(''),
  privacyUrl: z.string().default(''),
  video: VideoSchema.default({ url: '', title: '', duration: '', poster: '' }),
  consult: ConsultSchema.default(ConsultSchema.parse({})),
  domains: z.array(z.string()).default([]),
  regions: z.array(z.string()).default([]),
});
export type SiteContent = z.infer<typeof SiteContentSchema>;

/** Everything the admin edits as "settings". */
export const FairSettingsSchema = SiteContentSchema.extend(ScheduleSchema.shape).extend({
  webinarUrl: z.string().default(''),
  announcement: z.string().default(''),
});
export type FairSettings = z.infer<typeof FairSettingsSchema>;

/** Unit fields students never need. Stripped from the public snapshot. */
export type PublicUnit = Omit<Unit, 'status' | 'updatedAt' | 'updatedBy'>;

/** `data.<version>.json`: immutable, long-cached. */
export const SnapshotSchema = z.object({
  version: z.string(),
  generatedAt: z.number(),
  site: SiteContentSchema,
  units: z.array(UnitSchema.omit({ status: true, updatedAt: true, updatedBy: true })),
});
export type Snapshot = z.infer<typeof SnapshotSchema>;

/**
 * `state.json`: tiny, polled every few seconds, cached ~5s at the CDN.
 * Anything an admin may change at the last minute lives here, not in the snapshot.
 */
export const PublicStateSchema = ScheduleSchema.extend({
  /** Bumped on every publish. Clients refetch the snapshot when `dataVersion` changes. */
  rev: z.number(),
  dataVersion: z.string(),
  dataUrl: z.string(),
  webinarUrl: z.string().default(''),
  announcement: z.string().default(''),
});
export type PublicState = z.infer<typeof PublicStateSchema>;
